import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { makeStyles } from '@material-ui/core';
import { ListItem, Grid, InputBase } from '@material-ui/core';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';

import { useTranslation } from 'react-i18next';
import ReactTimeAgo from 'react-time-ago';

import ActionLink from './ActionLink';
import DeleteDialog from './DeleteDialog';

import i18n from '../../i18n.js';
import { useMutation } from 'react-query';
import { updateComment } from '../../api/objective/comment.api';
import { addAlert } from '../../actions/alerts.action';
import Linkify from '../Linkify';

const CommentItem = ({ objectiveId, comment, employeeId, onSubmitSuccess }) => {
  const lng = i18n.languages[0];
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [ content, setContent ] = useState(comment.content);
  const [ isEditing, setIsEditing ] = useState(false);
  const [ deleteDialogOpen, setDeleteDialogOpen ] = useState(false);

  const updateCommentMutation = useMutation(
    async () => {
      return await updateComment({
        employeeId,
        objectiveId,
        comment: {
          ...comment,
          content
        }
      });
    },
    {
      onSuccess: () => {
        onSubmitSuccess();
        setIsEditing(false);
      },
      onError: error => {
        dispatch(addAlert({ message: error.message, severity: 'error' }));
      }
    }
  );

  const handleOnChangeComment = event => {
    setContent(event.target.value);
  };

  const onDelete = () => {
    setDeleteDialogOpen(true);
  };

  const onUpdate = () => {
    setIsEditing(true);
  };

  const onCancel = () => {
    setIsEditing(false);
    setContent(comment.content);
  };

  const useStyles = makeStyles({
    listItem: {
      marginBottom: '10px'
    },
    comment: {
      margin: '15px 0',
      fontSize: '12px',
      color: '#666666'
    },
    updating: {
      borderRadius: '5px 5px 5px 5px',
      border: '1px solid #c9c9c9',
      padding: '10px'
    },
    nonUpdating: {
      borderStyle: 'none',
      color: '#666666',
      paddingLeft: '0'
    },
    info: {
      fontSize: '12px',
      color: '#999999',
      marginLeft: '15px'
    },
    lockIcon: {
      margin: 'auto auto -5px 15px',
      color: '#9FA2B4'
    }
  });

  const classes = useStyles();

  return (
    <ListItem className={ classes.listItem }>
      <Grid container direction={ 'column' }>
        <Grid item>
          <b>{ comment.commenter_name }</b>
          <ReactTimeAgo
            className={ classes.info }
            date={ new Date(comment.created_at) }
            locale={ lng }
          />
          { comment.edited_at !== null &&
            <>
              <span className={ classes.info }>
                |
              </span>
              <span className={ classes.info }>
                { t('objective.comment.edited') }
              </span>
            </>
          }
          { !comment.is_public &&
            <span>
              <LockOutlinedIcon className= { classes.lockIcon }/>
            </span>
          }
        </Grid>
        <Grid item className={ classes.comment }>
          {
            isEditing ?
              <InputBase
                value={ content }
                onChange={ handleOnChangeComment }
                classes={ { root: classes.updating, disabled: classes.nonUpdating } }
                multiline
                fullWidth
              /> :
              <Linkify>
                {comment.content}
              </Linkify>
          }
        </Grid>
        { comment.commented_by === employeeId &&
          <Grid>
            { isEditing ?
              <Grid item>
                <ActionLink type={ t('objective.comment.cancel') }  onClickFunction={ onCancel }/>
                <ActionLink
                  type={ t('objective.comment.save') }
                  onClickFunction={ updateCommentMutation.mutate }
                />
              </Grid> :
              <Grid item>
                <ActionLink type={ t('objective.comment.edit') }  onClickFunction={ onUpdate }/>
                <ActionLink
                  type={ t('objective.comment.delete') }
                  onClickFunction={ onDelete }
                />
              </Grid>
            }
            <DeleteDialog
              open={ deleteDialogOpen }
              setOpen={ setDeleteDialogOpen }
              commentId={ comment.id }
              objectiveId={ objectiveId }
              onSubmitSuccess={ onSubmitSuccess }
            />
          </Grid>
        }
      </Grid>
    </ListItem>
  );
};

export default CommentItem;
